'use client';
import type { ReactNode, Ref } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Textarea,
  type BoxProps,
  type FlexProps,
  type IconButtonProps,
  type TextareaProps,
} from '@chakra-ui/react';

export function ComposerShell(props: BoxProps) {
  return (
    <Box
      data-composer-shell
      bg="ui.surface.card"
      boxShadow="0 2px 12px rgba(0, 0, 0, 0.035)"
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="20px"
      overflow="visible"
      display="flex"
      flexDirection="column"
      w="full"
      maxW="760px"
      mx="auto"
      minH="112px"
      flexShrink={0}
      position="relative"
      containerType="inline-size"
      containerName="research-composer"
      _focusWithin={{ borderColor: 'ui.border.default' }}
      css={{
        '--composer-radius': '20px',
        '& [data-composer-control-label]': { fontWeight: 400 },
        '& textarea:is(:focus-visible, [data-focus-visible])': {
          outline: 'none !important',
          boxShadow: 'none !important',
        },
        '@container research-composer (max-width: 30rem)': {
          '& [data-composer-control-trigger]': {
            width: '28px',
            minWidth: '28px',
            maxWidth: '28px',
            paddingInline: '0',
            gap: '0',
            justifyContent: 'center',
          },
          '& [data-composer-control-label], & [data-composer-control-chevron]': { display: 'none' },
        },
      }}
      {...props}
    />
  );
}
export function ComposerInput(props: TextareaProps & { ref?: Ref<HTMLTextAreaElement> }) {
  return (
    <Textarea
      autoresize
      rows={1}
      minH="60px"
      maxH="200px"
      px={{ base: '3.5', md: '4' }}
      pt={{ base: '3.5', md: '4' }}
      pb="1"
      border="0"
      bg="transparent"
      resize="none"
      color="ui.text.primary"
      fontSize="sm"
      lineHeight="1.5"
      _placeholder={{ color: 'ui.text.tertiary' }}
      _focus={{ outline: 'none', boxShadow: 'none' }}
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      {...props}
    />
  );
}
export function ComposerToolbar(props: FlexProps) {
  return (
    <Flex
      px={{ base: '2.5', md: '3.5' }}
      pb={{ base: '2.5', md: '3' }}
      pt="0.5"
      mt="auto"
      gap="2"
      align="center"
      justify="space-between"
      flexShrink={0}
      {...props}
    />
  );
}
export function ComposerActionButton(props: IconButtonProps) {
  return (
    <IconButton
      size="sm"
      rounded="full"
      bg="ui.text.primary"
      color="ui.surface.card"
      _hover={{ opacity: 0.85 }}
      _disabled={{
        bg: 'ui.surface.selected',
        color: 'ui.text.tertiary',
        opacity: 1,
        cursor: 'not-allowed',
      }}
      {...props}
    />
  );
}

/** Both entry points use the same control groups and responsive spacing. */
export function ComposerControls({
  leading,
  trailing,
}: {
  leading: ReactNode;
  trailing: ReactNode;
}) {
  return (
    <ComposerToolbar>
      <Flex gap="1" minW={0} align="center">
        {leading}
      </Flex>
      <Flex gap="1" align="center" flexShrink={0}>
        {trailing}
      </Flex>
    </ComposerToolbar>
  );
}
